/* ------------------------------------------------------------------ *
 *  Badge contrast
 *
 *  The badge chip ("BARU", "BOD") is 10px uppercase on a translucent pill,
 *  so it needs 4.5:1 — and no single palette token delivers that across the
 *  themes. Measured against the chip's own background, `accent` falls below
 *  4.5:1 in 8 of the 12 dark sidebars (as low as 2.95:1 on `blue`, which is
 *  what made BARU hard to read), and `primary` still fails in 3.
 *
 *  So the palette supplies the hue and ensureContrast walks it toward white on
 *  a dark surface, or black on a light one, stopping the moment it clears the
 *  ratio. Themes whose token already passes come through untouched.
 *
 *  Shared by the sidebar badge and the doc-pabean "Kolom" button's Baru chip.
 * ------------------------------------------------------------------ */

export type Rgb = [number, number, number];

export const hexToRgb = (hex: string): Rgb => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [128, 128, 128];
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const rgbToCss = ([r, g, b]: Rgb) => `rgb(${r}, ${g}, ${b})`;

/** `src` laid over `dst` at `alpha`. */
const composite = (src: Rgb, alpha: number, dst: Rgb): Rgb =>
  [0, 1, 2].map((i) => Math.round(alpha * src[i] + (1 - alpha) * dst[i])) as Rgb;

const relativeLuminance = ([r, g, b]: Rgb): number => {
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export const contrastRatio = (a: Rgb, b: Rgb): number => {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Nudge `color` toward `toward` in 5% steps until it clears `target` contrast
 * against `bg`. Returns the first step that passes, or full `toward` if even
 * that cannot (which only happens when `bg` itself is mid-grey).
 */
export const ensureContrast = (color: string, bg: Rgb, toward: Rgb, target = 4.5): string => {
  const base = hexToRgb(color);
  if (contrastRatio(base, bg) >= target) return rgbToCss(base);
  for (let step = 0.05; step < 1; step += 0.05) {
    const mixed = composite(toward, step, base);
    if (contrastRatio(mixed, bg) >= target) return rgbToCss(mixed);
  }
  return rgbToCss(toward);
};

export const WHITE: Rgb = [255, 255, 255];
export const BLACK: Rgb = [0, 0, 0];
